import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MenuBar } from '@/components/common/MenuBar';
import { News } from '@/components/hotel/News';
import { MagazineParts } from '@/components/hotel/MagazineParts';
import { ReservationTable } from '@/components/monthly/ReservationTable';
import { FooterItems } from '@/components/common/FooterItems';
import { DetileTextImage } from '@/components/common/DetileTextImage';
import { LocationFetcher } from '@/features/Map/LocationFetcher';
import { getRoomTypeIndex } from '@/API/apiService';
import Picture from '@/image/hotel/picture10.png';
import sangubashiImage from '@/image/hotel/yoyogi.png';
import yoyogiImage from '@/image/hotel/sangubashi.png';
import shibuyaImage from '@/image/hotel/shibuyaTop.jpg';
import ebisuImage from '@/image/hotel/ebisuTop.jpg';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { DownArrowIcon } from '@/assets/DownArrowIcon';

interface RentalItem {
  id: number;
  room_type_id: number;
  start_date: string;
  end_date: string;
  created_at: string;
  updated_at: string | null;
}

/**
 * ヘルパー関数：テキスト内の「<br>」で分割し、各行の間に <br/> を挿入
 */
const formatText = (text?: string) => {
  if (!text) return '';
  const lines = text.split('<br>');
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index !== lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

/**
 * ヘルパー関数：施設情報やバスルームアメニティのデータをパースする
 * JSON形式（例: '["WiFi","Air Conditioning"]'）の場合はJSON.parseし、
 * そうでなければカンマ区切りの文字列として処理する
 */
const parseAmenityData = (rawData: string): string[] => {
  const trimmedData = rawData?.trim();
  if (!trimmedData) return [];
  if (trimmedData.startsWith('[')) {
    try {
      return JSON.parse(trimmedData);
    } catch (error) {
      console.error('JSON parse error:', error);
      return trimmedData.split(',').map((s: string) => s.trim());
    }
  }
  return trimmedData.split(',').map((s: string) => s.trim());
};

export const MonthlyDetail = () => {
  const { propertyId } = useParams<{ propertyId?: string }>();
  const location = useLocation();
  // URL の先頭セグメントから言語を判定 (例: /en/xxx なら 'en', それ以外は 'ja')
  const langSegment = location.pathname.split('/')[1];
  const language = langSegment === 'en' ? 'en' : 'ja';

  const [isLoading, setIsLoading] = useState(true);
  const [propertyInfo, setPropertyInfo] = useState<{
    image: string;
    area: string;
    name: string;
    subArea: string;
    monthlyRentals: RentalItem[];
    stationDistance: string;
    type: string;
    title: string;
    description: string;
  }>({
    image: '',
    area: '',
    name: '',
    subArea: '',
    stationDistance: '',
    type: '',
    title: '',
    description: '',
    monthlyRentals: [],
  });
  const [roomTypes, setRoomTypes] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!propertyId) {
        console.warn('propertyId がありません');
        setIsLoading(false);
        return;
      }

      try {
        const result = await getRoomTypeIndex(propertyId);
        const data = result?.data?.data;

        if (!data) {
          console.warn('No data returned from API');
          setIsLoading(false);
          return;
        }

        // propertyInfo更新 (言語に応じて _en カラムを利用)
        setPropertyInfo({
          image: data.property_image_url[0].image_url,
          area: language === 'ja' ? data.area_name : data.area_name_en,
          name: language === 'ja' ? data.property_name : data.property_name_en,
          monthlyRentals: data.monthlyRentals || [],
          subArea:
            data.sub_areas && data.sub_areas.length > 0
              ? language === 'ja'
                ? data.sub_areas.map((sa: any) => sa.name).join(',')
                : data.sub_areas.map((sa: any) => sa.name_en).join(',')
              : null,
          stationDistance:
            language === 'ja'
              ? data.station_distance
              : data.station_distance_en || '',
          type: 'The apartment Hotel',
          title: 'エリア1のタイトル',
          description:
            language === 'ja'
              ? (data.property_room_type_description ?? '説明なし')
              : (data.property_room_type_description_en ?? 'No description'),
        });

        // roomTypes更新
        const mappedRoomTypes = data.room_types
          ? data.room_types.map((room: any) => {
              // facility のパース処理
              console.log(room.facility_en, 'facility_en');
              const rawFacility =
                language === 'ja' ? room.facility : room.facility_en;
              const facilities = rawFacility
                ? parseAmenityData(rawFacility)
                : ['no amenity'];

              // bathAmenity のパース処理
              const rawBathAmenity =
                language === 'ja' ? room.bath_amenity : room.bath_amenity_en;
              const bathAmenities = rawBathAmenity
                ? parseAmenityData(rawBathAmenity)
                : ['no amenity'];

              // 両配列の要素から先頭と末尾のダブルクォーテーションを除去する関数
              const stripQuotes = (arr: string[]) =>
                arr.map((item) => item.replace(/^"+|"+$/g, ''));
              const facilityData =
                facilities && facilities.length > 0
                  ? stripQuotes(facilities)
                  : ['no amenity'];
              const bathAmenityData =
                bathAmenities && bathAmenities.length > 0
                  ? stripQuotes(bathAmenities)
                  : ['no amenity'];

              const monthlyRate = room.price || 'N/A';
              const dailyRate =
                room.price && !isNaN(parseFloat(room.price))
                  ? (parseFloat(room.price) / 30).toFixed(2)
                  : 'N/A';
              const floorPlanUrl = room.floor_plan_image?.image_url;
              let imageArray: { src: string; alt: string; text: string }[] = [
                {
                  src: Picture,
                  alt:
                    language === 'ja'
                      ? '画像がありません'
                      : 'No image available',
                  text:
                    language === 'ja'
                      ? '画像がありません'
                      : 'No image available',
                },
              ];
              if (room.images && room.images.length > 0) {
                imageArray = room.images.map((imgUrl: string) => ({
                  src: imgUrl,
                  alt:
                    language === 'ja'
                      ? room.name
                      : room.name_en || `Room type ${room.room_type_id}`,
                  text:
                    language === 'ja' ? room.information : room.information_en,
                }));
              }
              return {
                roomTypeId: room.room_type_id,
                monthlyRate,
                monthlyRentals: room.monthly_rentals,
                dailyRate,
                studioType:
                  language === 'ja' ? room.name : room.name_en || room.name,
                infoText:
                  language === 'ja' ? room.information : room.information_en,
                roomDescription:
                  language === 'ja'
                    ? (room.description ?? '説明なし')
                    : (room.description_en ?? 'No description'),
                images: imageArray,
                floorPlanUrl,
                facility: facilityData,
                bathAmenity: bathAmenityData, // 追加
              };
            })
          : [];
        setRoomTypes(mappedRoomTypes);
      } catch (error) {
        console.error('getRoomTypeIndex error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [propertyId, language]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // propertyId に応じた画像の上書き（例として /1 なら sangubashi、/2 なら yoyogi、/3 なら shibuyaImage、/4 なら ebisuImage）
  const displayImage =
    propertyId === '1'
      ? sangubashiImage
      : propertyId === '2'
        ? yoyogiImage
        : propertyId === '3'
          ? shibuyaImage
          : propertyId === '4'
            ? ebisuImage
            : propertyInfo.image;

  // meta タグの設定
  const metaDataMapping: Record<string, any> = {
    '1': {
      metaTitle:
        language === 'en'
          ? 'The Apartment Hotel Daikanyama | Shibuya-ku,Tokyo'
          : 'The Apartment Hotel 代官山',
      metaDescription:
        language === 'en'
          ? 'Located only 6 mins walk from Shibuya station, the residential area away from the hustle and bustle of Shibuya is home to cool shops and restaurants. Daikanyama and Ebisu are located just a short walk away, making the area perfect for a stroll on a sunny day.'
          : '徒歩で渋谷駅6分、代官山駅12分というアクセスながら、渋谷の喧騒から離れた住宅街。周辺には魅力的な飲食店が点在していて、少し歩くと代官山や恵比寿にも出られるので、晴れた日の街歩きが楽しい。',
      ogpTitle:
        language === 'en'
          ? 'The Apartment Hotel Daikanyama | Shibuya-ku,Tokyo'
          : 'The Apartment Hotel 代官山',
      ogpDescription:
        language === 'en'
          ? "6 mins walk from Shibuya station. We propose a hotel stay woven into Tokyo's neighborhoods. The Apartment Hotel combines easy accessibility from central Tokyo with a local stay in cool neighborhoods."
          : '渋谷駅徒歩6分。ネイバーフッドが豊かな旅を。都心へのアクセス性と魅力的な生活圏を兼ねそろえるアパートメントホテル。',
      ogpImage: 'URL_FOR_PROPERTY_1',
    },
    '2': {
      metaTitle:
        language === 'en'
          ? 'The Apartment Hotel Yoyogi | Shibuya-ku,Tokyo'
          : 'The Apartment Hotel 代々木',
      metaDescription:
        language === 'en'
          ? "Only a minute walk from Sangubashi station. While it is conveniently located close to Shinjuku, the hotel's neighborhood is home to individual shops such as bakeries, cafes, and restaurants adored by the local community. The trendy destinations like Yoyogi Koen and Yoyogi Uehara are within walking distance, making the area perfect for a stroll on a sunny day."
          : '徒歩で参宮橋駅1分、代々木公園駅15分。新宿エリアでアクセスが良好にも関わらず、地元の人に愛される個人店が多くあり、ベーカリー、カフェ、レストランなどを楽しむことができる。少し歩くとトレンドスポットが集まる代々木公園や代々木上原にも出られるので、晴れた日の街歩きが楽しい。',
      ogpTitle:
        language === 'en'
          ? 'The Apartment Hotel Yoyogi | Shibuya-ku,Tokyo'
          : 'The Apartment Hotel 代々木',
      ogpDescription:
        language === 'en'
          ? 'A minute walk from Sangubashi station near Shinjuku. Apply online for furnished rooms available with minimum one month contract.'
          : '参宮橋駅徒歩1分、新宿エリア。入退去はオンラインで完結・最短1ヶ月から、家具家電付きのお部屋を用意。',
      ogpImage: 'URL_FOR_PROPERTY_2',
    },
  };

  const currentMetaData = metaDataMapping[propertyId || '1'] || {};

  return (
    <>
      <Helmet>
        <title>{currentMetaData.metaTitle}</title>
        <meta name="description" content={currentMetaData.metaDescription} />
        <meta property="og:title" content={currentMetaData.ogpTitle} />
        <meta
          property="og:description"
          content={currentMetaData.ogpDescription}
        />
        <meta property="og:image" content={currentMetaData.ogpImage} />
        <meta property="og:type" content="website" />
      </Helmet>
      <MenuBar
        leftText={language === 'ja' ? 'Hotel Site' : 'Hotel Site EN'}
        rightText={language === 'ja' ? 'Rent Room' : 'Rent Room EN'}
        leftLink={`/hotel/${propertyId}`}
        rightLink="#rooms"
      >
        <div className="bg-whiteYellow relative w-full">
          <DetileTextImage
            imageSrc={displayImage}
            bottomTextPosition="bottom-left"
            bottomText={
              <div className="px-4 text-black">
                <h1 className="text-2xl pt-2">
                  {formatText(propertyInfo.name)}
                </h1>
                <p className="text-xs md:mt-5 mt-1">
                  {formatText(propertyInfo.stationDistance)}
                </p>
                <p className="text-[15px] pt-5">
                  {formatText(propertyInfo.description)}
                </p>
                <h1 className="text-[10px] mt-4">
                  {formatText(propertyInfo.area)}
                  {propertyInfo.subArea && (
                    <>, {formatText(propertyInfo.subArea)}</>
                  )}
                </h1>

                <div className="flex justify-start md:mt-8 mt-5 max-md:mb-4">
                  <DownArrowIcon />
                </div>
              </div>
            }
            imageTall
          />

          <div className="pl-3 mt-5 md:mt-20">
            <div id="rooms" className="mb-5 text-center text-lg">
              {language === 'ja' ? '(部屋タイプ)' : '(Room Types)'}
            </div>

            {roomTypes.length > 0 ? (
              roomTypes.map((room, index) => (
                <div
                  key={index}
                  className="mb-[6.25rem] border-t border-b border-l border-black md:ml-4 pl-4"
                >
                  <ReservationTable
                    isShow
                    studioType={room.studioType}
                    infoText={room.infoText}
                    monthlyRate={room.monthlyRate}
                    dailyRate={room.dailyRate}
                    images={room.images}
                    roomTypeId={room.roomTypeId}
                    roomDescription={room.roomDescription}
                    monthlyRentals={room.monthlyRentals}
                    floorPlanUrl={room.floorPlanUrl}
                    facility={room.facility}
                  />
                </div>
              ))
            ) : (
              <p className="text-center">部屋タイプがありません</p>
            )}
          </div>

          <div className="px-4">
            <div className="mb-[6.25rem]">
              <LocationFetcher propertyId={propertyId} />
            </div>
            <div className="mb-[6.25rem]">
              <MagazineParts />
            </div>
            <div className="md:px-15 px-4">
              <div className="mb-[6.25rem]">
                <News />
              </div>
            </div>
            <FooterItems />
          </div>
        </div>
      </MenuBar>
    </>
  );
};

export default MonthlyDetail;
